/**
 * Helper function to safely extract URL and duration from various upload data structures
 *
 * Handles multiple data formats:
 * 1. String - direct URL path from backend
 * 2. Deeply nested object - { rawFile: { url: { url: "path", duration: 123 }, ... }, src, title }
 * 3. Shallow nested object - { url: "path", duration: 123 }
 * 4. File object - throws error if file is still uploading
 *
 * @param {*} urlData - The data that may contain a URL (string, object, or File)
 * @param {string} fieldName - Name of the field for error messages (default: 'url')
 * @returns {{ url: string|null, duration: number|null }} Extracted URL and duration
 * @throws {string} Error message if file is still uploading
 */
export const extractUrlFromUploadData = (urlData, fieldName = 'url') => {
  // If it's already a string, return it directly
  if (typeof urlData === 'string') {
    return { url: urlData, duration: null }
  }

  // If it's null or undefined
  if (!urlData) {
    return { url: null, duration: null }
  }

  // If it's a File object (typeof File === 'object' but instanceof File === true)
  // Check this BEFORE generic object check since File is also an object
  if (urlData instanceof File || urlData instanceof Blob) {
    // File hasn't been uploaded yet or is still uploading
    throw `${fieldName} file is still uploading...`
  }

  // If it's an object, try to extract the nested path
  if (typeof urlData === 'object') {
    // Check for CustomFileInput upload structure: { rawFile: File, src, title, url: { url, duration } }
    // This occurs when CustomFileInput stores upload result at file[source] = { url, duration }
    if (urlData.url && typeof urlData.url === 'object' && urlData.url.url) {
      return {
        url: urlData.url.url,
        duration: urlData.url.duration || null,
      }
    }

    // Check for deeply nested structure: urlData.rawFile.url.url
    // This occurs after CustomFileInput uploads with the transformed file structure
    if (urlData.rawFile?.url?.url) {
      return {
        url: urlData.rawFile.url.url,
        duration: urlData.rawFile.url.duration || null,
      }
    }

    // Check if still uploading (rawFile exists but no upload result yet)
    // Only throw if rawFile is a File/Blob AND there's no url property with result
    if (
      urlData.rawFile &&
      (urlData.rawFile instanceof File || urlData.rawFile instanceof Blob) &&
      !urlData.url
    ) {
      throw `${fieldName} file is still uploading...`
    }

    // Check if still uploading (rawFile is an object but url.url doesn't exist)
    if (
      urlData.rawFile &&
      !(urlData.rawFile instanceof File) &&
      !(urlData.rawFile instanceof Blob) &&
      !urlData.rawFile.url
    ) {
      throw `${fieldName} file is still uploading...`
    }

    // Check for shallow nested structure: urlData.url
    // This can occur with different upload configurations
    if (urlData.url && typeof urlData.url === 'string') {
      return {
        url: urlData.url,
        duration: urlData.duration || null,
      }
    }
  }

  // If we can't extract a valid URL, log warning and return null
  console.warn(`Unable to extract URL from ${fieldName}:`, urlData)
  return { url: null, duration: null }
}

/**
 * Extract audio path for display in audio player components
 * Supports multiple data formats and can create blob URLs for immediate preview
 *
 * @param {*} value - The value that may contain an audio path
 * @returns {string|null} The audio path (string URL or blob URL) or null
 */
export const extractAudioPath = value => {
  if (!value) return null

  // Handle string path (from backend/saved data)
  if (typeof value === 'string') return value

  // Handle deeply nested upload structure: value.rawFile.url.url
  if (value.rawFile?.url?.url && typeof value.rawFile.url.url === 'string') {
    return value.rawFile.url.url
  }

  // Handle shallow nested structure: value.url
  if (value.url && typeof value.url === 'string') return value.url

  // Handle File object for immediate preview (before upload completes)
  // Check for deeply nested File: value.rawFile.rawFile
  if (value.rawFile?.rawFile instanceof File) {
    return URL.createObjectURL(value.rawFile.rawFile)
  }

  // Check for single level nested File: value.rawFile
  if (value.rawFile instanceof File) {
    return URL.createObjectURL(value.rawFile)
  }

  // Handle direct File object
  if (value instanceof File) {
    return URL.createObjectURL(value)
  }

  return null
}
